import { Button, Checkbox, FormControlLabel, Menu, Stack, Typography } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { getOrderAsPDF, getOrderAsExcel } from '../../../api/rentals';
import Iconify from '../../../reusable-components/iconify';
import { useSnackbar } from '../../../reusable-components/snackbar';

DownloadRentalOrderDropdown.propTypes = {
  orderId: PropTypes.number,
  rows: PropTypes.array,
  isPunchoutOrder: PropTypes.bool,
};

export default function DownloadRentalOrderDropdown({ orderId, rows, isPunchoutOrder }) {
  const { enqueueSnackbar } = useSnackbar();

  const [anchorEl, setAnchorEl] = useState(null);
  const [vendorIds, setVendorIds] = useState([]);
  const [useAllVendors, setUseAllVendors] = useState(false);
  const [downloadFormat, setDownloadFormat] = useState('pdf');
  const open = Boolean(anchorEl);

  const vendors = useMemo(() => {
    if (!rows) return [];
    const vendorArray = rows?.map((item) => ({ vendor: item.vendor, vendorId: item.vendorId }));
    const uniqueVendors = Array.from(new Set(vendorArray.map((v) => v.vendorId))).map((id) =>
      vendorArray.find((v) => v.vendorId === id)
    );
    return uniqueVendors;
  }, [orderId]);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setVendorIds([]);
    setUseAllVendors(false);
    setDownloadFormat('pdf');
  };

  const downloadOrder = async () => {
    const itemsToAdd = rows.filter((i) => vendorIds.includes(i.vendorId));
    const cartIds = itemsToAdd.map((i) => i.cartItemId);

    if (itemsToAdd.length > 0) {
      const res =
        downloadFormat === 'pdf'
          ? await getOrderAsPDF({ orderId, items: cartIds, isPunchoutOrder }).catch(() =>
              enqueueSnackbar('Order PDF failed to download', { variant: 'error' })
            )
          : await getOrderAsExcel({ orderId, items: cartIds, isPunchoutOrder }).catch(() =>
              enqueueSnackbar('Order Excel failed to download', { variant: 'error' })
            );

      setAnchorEl(null);

      if (res.status === 200) {
        const blob = new Blob([res.data], { type: `application/${downloadFormat}` });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${orderId}.${downloadFormat}`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      }
    } else {
      enqueueSnackbar('No vendor selected', { variant: 'warning' });
    }
  };

  useEffect(() => {
    if (useAllVendors) setVendorIds(vendors.map((vendor) => vendor.vendorId));
    else setVendorIds([]);
  }, [useAllVendors, vendors]);

  return (
    <>
      <Button
        size="small"
        variant="outlined"
        color="secondary"
        onClick={handleClick}
        sx={{ marginRight: '10px' }}
        endIcon={<Iconify icon="mdi:chevron-down" />}
      >
        Download
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        slotProps={{
          paper: {
            sx: {
              width: 269,
              p: 1,
              borderRadius: '5px',
              boxShadow: '0px 2px 6px rgba(0,0,0,.06), 0px 8px 24px rgba(0,0,0,.12)',
            },
          },
        }}
      >
        <Stack spacing={1.5}>
          <Stack spacing={0.25} sx={{ pt: 0.5 }}>
            <Typography sx={{ fontSize: '14px', color: 'secondary.main', letterSpacing: 0.4, pl: 1 }}>
              Download by Vendor
            </Typography>
            <FormControlLabel
              sx={{ ml: 0, '& .MuiFormControlLabel-label': { fontSize: '0.875rem', color: 'grey.700' } }}
              control={
                <Checkbox size="small" checked={useAllVendors} onChange={() => setUseAllVendors((useAll) => !useAll)} />
              }
              label="All Vendors"
            />
            {vendors?.map((vendor) => (
              <FormControlLabel
                key={vendor.vendorId}
                sx={{ ml: 0, '& .MuiFormControlLabel-label': { fontSize: '0.875rem', color: 'grey.700' } }}
                control={
                  <Checkbox
                    size="small"
                    checked={vendorIds?.includes(vendor.vendorId)}
                    onChange={() =>
                      vendorIds?.includes(vendor.vendorId)
                        ? setVendorIds(vendorIds?.filter((v) => v !== vendor.vendorId))
                        : setVendorIds([...vendorIds, vendor.vendorId])
                    }
                  />
                }
                label={vendor.vendor}
              />
            ))}
          </Stack>

          <Stack spacing={0.25}>
            <Typography sx={{ fontSize: '14px', color: 'secondary.main', letterSpacing: 0.4, pl: 1 }}>
              Format
            </Typography>
            <FormControlLabel
              sx={{ ml: 0, '& .MuiFormControlLabel-label': { fontSize: '0.875rem', color: 'grey.700' } }}
              control={
                <Checkbox
                  size="small"
                  checked={downloadFormat === 'pdf'}
                  onChange={() => setDownloadFormat(downloadFormat === 'pdf' ? 'xlsx' : 'pdf')}
                />
              }
              label="PDF"
            />
            <FormControlLabel
              sx={{ ml: 0, '& .MuiFormControlLabel-label': { fontSize: '0.875rem', color: 'grey.700' } }}
              control={
                <Checkbox
                  size="small"
                  checked={downloadFormat === 'xlsx'}
                  onChange={() => setDownloadFormat(downloadFormat === 'pdf' ? 'xlsx' : 'pdf')}
                />
              }
              label="Excel"
            />
          </Stack>

          <Button
            fullWidth
            variant="contained"
            color="secondary"
            onClick={downloadOrder}
            startIcon={<Iconify icon="mdi:download" />}
            sx={{ borderRadius: 1, fontSize: '0.875rem', py: 0.75 }}
          >
            Download
          </Button>
        </Stack>
      </Menu>
    </>
  );
}
